/**
 * Affiliate attribution: last-touch within a 30-day window.
 *
 * The affiliate id stored on the client is an opaque short-code controlled
 * by the affiliate program (e.g. "alice1234"). It is NOT a UUID, so we
 * validate as alphanumeric + hyphen/underscore, 3..40 chars.
 */

import { tenantStorage } from './storage';

export const AFFILIATE_ID_REGEX = /^[A-Za-z0-9_-]{3,40}$/;
export const ATTRIBUTION_KEY = 'affiliate_attribution';
export const ATTRIBUTION_WINDOW_MS = 30 * 24 * 60 * 60 * 1000;

export interface AttributionRecord {
  affiliateId: string;
  timestamp: number;
}

export function isValidAffiliateId(value: unknown): value is string {
  return typeof value === 'string' && AFFILIATE_ID_REGEX.test(value);
}

/**
 * Pre-tenant-scope legacy keys (`affiliateId` + `affiliateTimestamp`) were
 * written to raw `window.localStorage` by older builds, with no tenant
 * prefix. We read them via raw localStorage one last time so users who
 * upgraded with state in place don't lose attribution. Once migrated, the
 * raw keys are deleted; new state always goes through tenantStorage.
 */
function readLegacyUnprefixed(name: string): string | null {
  try {
    return window.localStorage.getItem(name);
  } catch {
    return null;
  }
}
function removeLegacyUnprefixed(name: string): void {
  try {
    window.localStorage.removeItem(name);
  } catch {
    // ignore
  }
}

export function readAttribution(): string | null {
  // Migrate legacy storage shape (`affiliateId` + `affiliateTimestamp`,
  // pre-tenant-scope unprefixed) into the unified JSON record once, then
  // read only the new tenant-scoped key.
  const legacyId = readLegacyUnprefixed('affiliateId');
  const legacyTs = readLegacyUnprefixed('affiliateTimestamp');
  if (legacyId && legacyTs && !tenantStorage.getItem(ATTRIBUTION_KEY)) {
    const ts = Number.parseInt(legacyTs, 10);
    if (isValidAffiliateId(legacyId) && Number.isFinite(ts)) {
      tenantStorage.setItem(
        ATTRIBUTION_KEY,
        JSON.stringify({ affiliateId: legacyId, timestamp: ts }),
      );
    }
    removeLegacyUnprefixed('affiliateId');
    removeLegacyUnprefixed('affiliateTimestamp');
  }

  const raw = tenantStorage.getItem(ATTRIBUTION_KEY);
  if (!raw) return null;
  let record: AttributionRecord;
  try {
    record = JSON.parse(raw);
  } catch {
    tenantStorage.removeItem(ATTRIBUTION_KEY);
    return null;
  }
  if (
    !isValidAffiliateId(record?.affiliateId) ||
    typeof record.timestamp !== 'number'
  ) {
    tenantStorage.removeItem(ATTRIBUTION_KEY);
    return null;
  }
  if (Date.now() - record.timestamp > ATTRIBUTION_WINDOW_MS) {
    tenantStorage.removeItem(ATTRIBUTION_KEY);
    return null;
  }
  return record.affiliateId;
}

export function writeAttribution(affiliateId: string): void {
  if (!isValidAffiliateId(affiliateId)) return;
  const record: AttributionRecord = { affiliateId, timestamp: Date.now() };
  tenantStorage.setItem(ATTRIBUTION_KEY, JSON.stringify(record));
}
